/**
 * AudienceSelector Component
 * Modal to search and select available audiences for targeting
 *
 * Features:
 * - Search by audience name
 * - Filter by type (User List, Custom Audience, Combined Audience)
 * - Multi-select with checkboxes
 * - Hides already-targeted audiences (excluded by ID or name)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge, LoadingSpinner } from '@/components/ui';
import { campaignsApi } from '@/services/api/campaigns';

interface AvailableAudience {
  resource_name: string;
  audience_id: string;
  audience_name: string;
  audience_type: string;
  description?: string;
}

interface AudienceSelectorProps {
  campaignId: string;
  onSelect: (audiences: Array<{ resource_name: string; audience_name: string }>) => void;
  onClose: () => void;
  /** Audience IDs to exclude (matches against AvailableAudience.audience_id) */
  excludedAudienceIds?: string[];
  /** Audience names to exclude (fallback for Custom/Combined audiences without IDs) */
  excludedAudienceNames?: string[];
}

const AUDIENCE_TYPE_FILTERS = [
  { value: 'USER_LIST', label: 'User Lists' },
  { value: 'CUSTOM_AUDIENCE', label: 'Custom Audiences' },
  { value: 'COMBINED_AUDIENCE', label: 'Combined Audiences' },
];

// Helper to get badge color for audience type
function getTypeColor(type: string): string {
  const colorMap: Record<string, string> = {
    'USER_LIST': 'bg-blue-100 text-blue-800',
    'CUSTOM_AUDIENCE': 'bg-indigo-100 text-indigo-800',
    'COMBINED_AUDIENCE': 'bg-violet-100 text-violet-800',
  };
  return colorMap[type] || 'bg-gray-100 text-gray-800';
}

function formatAudienceType(type: string): string {
  const typeMap: Record<string, string> = {
    'USER_LIST': 'User List',
    'CUSTOM_AUDIENCE': 'Custom',
    'COMBINED_AUDIENCE': 'Combined',
  };
  return typeMap[type] || type;
}

export function AudienceSelector({
  campaignId,
  onSelect,
  onClose,
  excludedAudienceIds = [],
  excludedAudienceNames = [],
}: AudienceSelectorProps) {
  const [availableAudiences, setAvailableAudiences] = useState<AvailableAudience[]>([]);
  const [selectedAudiences, setSelectedAudiences] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Body scroll locking and focus management
  useEffect(() => {
    // Store original body styles and active element
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const previouslyFocusedElement = document.activeElement as HTMLElement;

    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Lock body scroll and compensate for scrollbar width
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Focus the search input when modal opens
    searchInputRef.current?.focus();

    // Cleanup: restore original styles and focus
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      previouslyFocusedElement?.focus();
    };
  }, []);

  // Focus trap: keep focus within the modal
  useEffect(() => {
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, []);

  // Fetch available audiences
  useEffect(() => {
    const fetchAudiences = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await campaignsApi.getAvailableAudiences(campaignId);
        setAvailableAudiences(data.audiences || []);
      } catch (err) {
        console.error('Failed to fetch available audiences:', err);
        setError('Failed to load available audiences');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAudiences();
  }, [campaignId]);

  // Filter audiences based on search and type
  // Excludes audiences by ID or by name (for Custom/Combined audiences without IDs)
  const filteredAudiences = useMemo(() => {
    return availableAudiences
      .filter(a => !excludedAudienceIds.includes(a.audience_id))
      .filter(a => !excludedAudienceNames.includes(a.audience_name))
      .filter(a => !searchQuery || a.audience_name.toLowerCase().includes(searchQuery.toLowerCase()))
      .filter(a => !typeFilter || a.audience_type === typeFilter);
  }, [availableAudiences, searchQuery, typeFilter, excludedAudienceIds, excludedAudienceNames]);

  // Handle checkbox toggle
  const handleToggle = useCallback((resourceName: string) => {
    setSelectedAudiences(prev => {
      const next = new Set(prev);
      if (next.has(resourceName)) {
        next.delete(resourceName);
      } else {
        next.add(resourceName);
      }
      return next;
    });
  }, []);

  // Handle select all visible
  const handleSelectAll = useCallback(() => {
    const allVisible = filteredAudiences.map(a => a.resource_name);
    setSelectedAudiences(new Set(allVisible));
  }, [filteredAudiences]);

  // Handle clear selection
  const handleClearSelection = useCallback(() => {
    setSelectedAudiences(new Set());
  }, []);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (selectedAudiences.size === 0) return;

    setIsSubmitting(true);

    const audiencesToAdd = availableAudiences
      .filter(a => selectedAudiences.has(a.resource_name))
      .map(a => ({ resource_name: a.resource_name, audience_name: a.audience_name }));

    try {
      await onSelect(audiencesToAdd);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedAudiences, availableAudiences, onSelect]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="audience-selector-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 id="audience-selector-title" className="text-lg font-semibold text-gray-900">Select Audiences</h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              aria-label="Close dialog"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search and Filters */}
          <div className="p-4 border-b space-y-3">
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search audiences..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
              aria-label="Search audiences"
            />

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTypeFilter(null)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  typeFilter === null
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Types
              </button>
              {AUDIENCE_TYPE_FILTERS.map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setTypeFilter(filter.value)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    typeFilter === filter.value
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-4 max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">{error}</div>
            ) : filteredAudiences.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchQuery || typeFilter
                  ? 'No audiences match your filters'
                  : 'No audiences available in this account'}
              </div>
            ) : (
              <>
                {/* Selection controls */}
                <div className="flex items-center justify-between mb-3 text-sm text-gray-600">
                  <span>{filteredAudiences.length} audiences available</span>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSelectAll}
                      className="text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Select All
                    </button>
                    {selectedAudiences.size > 0 && (
                      <button
                        onClick={handleClearSelection}
                        className="text-gray-500 hover:text-gray-600 hover:underline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                {/* Audience list */}
                <div className="space-y-2">
                  {filteredAudiences.map(audience => (
                    <label
                      key={audience.resource_name}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedAudiences.has(audience.resource_name)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox
                        checked={selectedAudiences.has(audience.resource_name)}
                        onChange={() => handleToggle(audience.resource_name)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">
                            {audience.audience_name}
                          </span>
                          <Badge className={`flex-shrink-0 px-2 py-0.5 text-xs ${getTypeColor(audience.audience_type)}`}>
                            {formatAudienceType(audience.audience_type)}
                          </Badge>
                        </div>
                        {audience.description && (
                          <p className="text-sm text-gray-500 mt-1 truncate" title={audience.description}>
                            {audience.description}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50 rounded-b-lg">
            <span className="text-sm text-gray-600">
              {selectedAudiences.size} selected
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={selectedAudiences.size === 0 || isSubmitting}
              >
                {isSubmitting ? 'Adding...' : `Add ${selectedAudiences.size} Audience${selectedAudiences.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
